import React, { forwardRef, useId } from 'react';
import { ChevronDown } from 'lucide-react';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectOptionGroup {
  label: string;
  options: SelectOption[];
}

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  label?: string;
  error?: string;
  helpText?: string;
  options: SelectOption[];
  groups?: SelectOptionGroup[];
  placeholder?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, helpText, options, groups, placeholder, className = '', id, ...props }, ref) => {
    const autoId = useId();
    const selectId = id || autoId;

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={selectId}
            className="block text-sm font-medium text-neutral-200 mb-1.5"
          >
            {label}
            {props.required && <span className="text-red-400 ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            className={`
              w-full
              bg-neutral-900/50
              border
              ${error ? 'border-red-500' : 'border-neutral-700'}
              rounded-lg
              px-3 py-2
              min-h-[44px] sm:min-h-0
              pr-10
              text-neutral-50
              focus:outline-none
              focus:border-primary-500
              focus:ring-2
              focus:ring-primary-500/20
              transition-all duration-200
              disabled:opacity-50
              disabled:cursor-not-allowed
              appearance-none
              cursor-pointer
              ${className}
            `}
            {...props}
          >
            {placeholder && (
              <option value="" disabled className="text-neutral-500">
                {placeholder}
              </option>
            )}
            {groups
              ? groups.map((group) => (
                  <optgroup key={group.label} label={group.label} className="bg-neutral-900 text-neutral-400">
                    {group.options.map((option) => (
                      <option
                        key={option.value}
                        value={option.value}
                        disabled={option.disabled}
                        className="bg-neutral-900 text-neutral-50"
                      >
                        {option.label}
                      </option>
                    ))}
                  </optgroup>
                ))
              : options.map((option) => (
                  <option
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                    className="bg-neutral-900 text-neutral-50"
                  >
                    {option.label}
                  </option>
                ))}
          </select>

          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-neutral-500">
            <ChevronDown className="w-4 h-4" />
          </div>
        </div>

        {helpText && !error && (
          <p className="mt-1 text-xs text-neutral-500">{helpText}</p>
        )}

        {error && (
          <p className="mt-1 text-xs text-red-400">{error}</p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';
